'use client';

import { useState, useEffect, useRef, forwardRef } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Input } from '../forms/Input';
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../../ui';

export const DatePickerV2 = forwardRef(
  (
    {
      initialDate = null,
      onChange,
      disabled = false,
      required = false,
      label,
      hint
    },
    ref
  ) => {
    // Controlled so the calendar can always be dismissed. Left uncontrolled, the
    // Radix default outside-press dismissal does not fire reliably when the picker
    // is opened inside a modal, which left the calendar stuck open with no way to
    // close it except tabbing away or clicking another control.
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState(initialDate);
    const [selectedYear, setSelectedYear] = useState(
      initialDate ? initialDate.getFullYear() : new Date().getFullYear()
    );
    const [yearViewCenter, setYearViewCenter] = useState(
      initialDate ? initialDate.getFullYear() : new Date().getFullYear()
    );
    const [calendarMonth, setCalendarMonth] = useState(
      initialDate || new Date()
    );

    const [day, setDay] = useState(
      initialDate ? initialDate.getDate().toString() : ''
    );
    const [month, setMonth] = useState(
      initialDate ? (initialDate.getMonth() + 1).toString() : ''
    );
    const [year, setYear] = useState(
      initialDate ? initialDate.getFullYear().toString() : ''
    );

    const dayInputRef = useRef(null);
    const monthInputRef = useRef(null);
    const yearInputRef = useRef(null);

    // The trigger stays in place; the content is portalled to body, so a press is
    // only "outside" when it is in neither.
    const wrapperRef = useRef(null);
    const contentRef = useRef(null);

    // Radix dismisses on outside press via a listener on document, but this picker is
    // often opened inside a Headless UI Dialog, which runs its own outside-click
    // handling and swallows the press before Radix sees it — so onInteractOutside
    // never fires. Detect it here instead, in the CAPTURE phase on document, which
    // runs before anything in the tree can stop the event.
    useEffect(() => {
      if (!open) return undefined;

      const handlePointerDown = event => {
        const target = event.target;
        if (contentRef.current?.contains(target)) return;
        if (wrapperRef.current?.contains(target)) return;
        setOpen(false);
      };

      document.addEventListener('pointerdown', handlePointerDown, true);
      return () =>
        document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [open]);

    useEffect(() => {
      setDate(initialDate);
      if (initialDate) {
        setSelectedYear(initialDate.getFullYear());
        setYearViewCenter(initialDate.getFullYear());
        setCalendarMonth(initialDate);
        setDay(initialDate.getDate().toString());
        setMonth((initialDate.getMonth() + 1).toString());
        setYear(initialDate.getFullYear().toString());
      }
    }, [initialDate]);

    const handleSelect = selectedDate => {
      if (selectedDate) {
        // Fix timezone issue by setting time to noon
        const normalizedDate = new Date(selectedDate);
        normalizedDate.setHours(12, 0, 0, 0);

        setDate(normalizedDate);
        setDay(normalizedDate.getDate().toString());
        setMonth((normalizedDate.getMonth() + 1).toString());
        setYear(normalizedDate.getFullYear().toString());

        if (onChange) {
          onChange(normalizedDate);
        }
      } else {
        setDate(null);
        setDay('');
        setMonth('');
        setYear('');

        if (onChange) {
          onChange(null);
        }
      }
    };

    const handleYearSelect = year => {
      // Create a new date for the calendar view only
      const newCalendarMonth = new Date(calendarMonth);
      newCalendarMonth.setFullYear(year);

      setCalendarMonth(newCalendarMonth);
      setSelectedYear(year);
    };

    const updateDateFromInputs = (
      currentDayStr = day,
      currentMonthStr = month,
      currentYearStr = year
    ) => {
      if (currentDayStr && currentMonthStr && currentYearStr) {
        const dayNum = parseInt(currentDayStr, 10);
        const monthNum = parseInt(currentMonthStr, 10) - 1; // JS months are 0-indexed
        const yearNum = parseInt(currentYearStr, 10);

        // Validate date
        if (
          dayNum > 0 &&
          dayNum <= 31 &&
          monthNum >= 0 &&
          monthNum <= 11 &&
          yearNum > 0
        ) {
          const newDate = new Date(yearNum, monthNum, dayNum, 12, 0, 0, 0);

          // Check if the date is valid (handles cases like Feb 30)
          if (newDate.getDate() === dayNum && newDate.getMonth() === monthNum) {
            setDate(newDate);
            setCalendarMonth(newDate);
            setSelectedYear(yearNum);
            setYearViewCenter(yearNum);

            if (onChange) {
              onChange(newDate);
            }
          }
        }
      }
    };

    const handleDayChange = e => {
      const value = e.target.value;
      if (value === '' || /^\d{1,2}$/.test(value)) {
        setDay(value);

        // Auto-tab to month when 2 digits entered
        if (value.length === 2) {
          monthInputRef.current?.focus();
        }
      }
    };

    const handleMonthChange = e => {
      const value = e.target.value;
      if (value === '' || /^\d{1,2}$/.test(value)) {
        setMonth(value);

        // Auto-tab to year when 2 digits entered
        if (value.length === 2) {
          yearInputRef.current?.focus();
        }
      }
    };

    const handleYearChange = e => {
      const value = e.target.value;
      if (value === '' || /^\d{1,4}$/.test(value)) {
        setYear(value);

        // Update date when year is complete
        if (value.length === 4) {
          updateDateFromInputs(day, month, value);
        }
      }
    };

    const handleInputBlur = () => {
      updateDateFromInputs();
    };

    const renderYearCarousel = () => {
      const prevYear = yearViewCenter - 1;
      const nextYear = yearViewCenter + 1;

      return (
        <div className='flex justify-between items-center p-2 border-b'>
          <Button
            variant='ghost'
            onClick={() => setYearViewCenter(yearViewCenter - 3)}
            className='h-7 w-7 bg-transparent p-0'
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <div className='flex space-x-2'>
            <Button
              variant={prevYear === selectedYear ? 'default' : 'ghost'}
              className='h-9'
              onClick={() => handleYearSelect(prevYear)}
            >
              {prevYear}
            </Button>
            <Button
              variant={yearViewCenter === selectedYear ? 'default' : 'ghost'}
              className='h-9'
              onClick={() => handleYearSelect(yearViewCenter)}
            >
              {yearViewCenter}
            </Button>
            <Button
              variant={nextYear === selectedYear ? 'default' : 'ghost'}
              className='h-9'
              onClick={() => handleYearSelect(nextYear)}
            >
              {nextYear}
            </Button>
          </div>
          <Button
            variant='ghost'
            onClick={() => setYearViewCenter(yearViewCenter + 3)}
            className='h-7 w-7 bg-transparent p-0'
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      );
    };

    return (
      <div
        className='w-full'
        // Composite: keeps the forwarded ref working while also giving the
        // outside-press check something to test the trigger against.
        ref={node => {
          wrapperRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
      >
        {label && (
          <div className='flex justify-between'>
            <label className='block text-sm font-medium leading-6 text-gray-900'>
              {label}
              {required && <span className='text-danger-700 ml-1'>*</span>}
            </label>
          </div>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={'outline'}
              className={cn(
                'w-full justify-start text-left font-normal px-2.5 py-1.5 text-sm',
                !date && 'text-muted-foreground',
                required && !date && 'border-red-500',
                disabled && 'cursor-not-allowed'
              )}
              disabled={disabled}
            >
              <CalendarIcon className='mr-2 h-4 w-4' />
              {date ? (
                format(date, 'do MMMM yyyy')
              ) : (
                <span>Pick a date{required && '*'}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            ref={contentRef}
            className='w-auto p-0'
            onEscapeKeyDown={() => setOpen(false)}
          >
            <div>
              {renderYearCarousel()}
              <div className='flex p-3 gap-2 border-b justify-center'>
                <Input
                  ref={dayInputRef}
                  placeholder='DD'
                  value={day}
                  onChange={handleDayChange}
                  onBlur={handleInputBlur}
                  className='max-w-16'
                  maxLength={2}
                  disabled={disabled}
                />

                <Input
                  ref={monthInputRef}
                  placeholder='MM'
                  value={month}
                  onChange={handleMonthChange}
                  onBlur={handleInputBlur}
                  className='max-w-16'
                  maxLength={2}
                  disabled={disabled}
                />
                <Input
                  ref={yearInputRef}
                  placeholder='YYYY'
                  value={year}
                  onChange={handleYearChange}
                  onBlur={handleInputBlur}
                  className='max-w-20'
                  maxLength={4}
                  disabled={disabled}
                />
              </div>
              <Calendar
                mode='single'
                selected={date}
                // Picking a day is the end of the interaction, so close on it. The
                // DD/MM/YYYY inputs above deliberately do not close, so a date can
                // still be typed out in full.
                onSelect={selectedDate => {
                  handleSelect(selectedDate);
                  if (selectedDate) setOpen(false);
                }}
                initialFocus
                disabled={disabled}
                defaultMonth={calendarMonth}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
              />
              {/* Always-present way out, for typing a date rather than clicking one
                  and for anywhere the outside press still does not land. */}
              <div className='flex justify-end border-t p-2'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setOpen(false)}
                  type='button'
                >
                  Done
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        {hint && <p className='mt-1 text-sm text-gray-500'>{hint}</p>}
      </div>
    );
  }
);

DatePickerV2.displayName = 'DatePickerV2';
